import React, { useEffect, useState } from 'react';
import { useZone } from '../../hooks/zone';
import { XDrawerBottom } from '../../components/x/drawer-bottom/XDrawerBottom';
import { useAsync } from '../../hooks/useAsync';
import { apiSpcPublicCheckoutPost } from '../../api/spc.public.Checkout.[post]';
import { SpcCheckoutChooseProducts } from './choose-products/SpcCheckoutChooseProducts';
import { SpcCheckoutChooseTip } from './choose-tip/SpcCheckoutChooseTip';
import { SpcCheckoutChoosePaymentProvider } from './choose-payment-provider/SpcCheckoutChoosePaymentProvider';
import { XLoader } from '../../components/x/loader/XLoader';
import { UiGrid } from '@open-stack/ui';
import { useActiveOrder } from '../../hooks/active-order';
import styled from 'styled-components';
import { SpcCheckoutOrderPlaced } from './order-placed/SpcCheckoutOrderPlaced';
import { SpcCheckoutAcceptTerms } from './accept-terms/SpcCheckoutAcceptTerms';
import { useTermsPrivacy } from '../../hooks/terms-privacy/useTermsPrivacy';
import { XOverlay } from '../../components/x/overlay/XOverlay';
import { useRouter } from '../../hooks/router';
import { OrderIdPage } from '../../pages/order/id/OrderIdPage';
import { Environment } from '../../environment';
import { SpcCheckoutChangeCourse } from './change-course/SpcCheckoutChangeCourse';
import { isMobile } from '../../util/isMobile';
import { SpcCheckoutHeader } from './header/SpcCheckoutHeader';
import { ProductType } from '../../XV2';
import { SpcCheckoutPhone } from './phone/SpcCheckoutPhone';
import { XText, XTextType } from '../../components/x/text/XText';

const Wrapper = styled.div`
    .beta {
        position: fixed;
        top: 4px;
        right: 4px;
        height: 4px;
        width: 4px;
        background-color: var(--x-color-primary);
        z-index: 100000;
        border-radius: 100%;
    }
`;

const Sequence = styled.div`
    padding: var(--XR-gap8);
    text-align: center;

    label {
        font-size: 6rem;
        letter-spacing: 4px;
    }

    p {
        font-size: 1.25rem;
        margin: 0;
    }
`;

enum Action {
    PLACE_ORDER = 'PLACE_ORDER',
    CHOOSE_PRODUCTS = 'CHOOSE_PRODUCTS',
    CHOOSE_TIP = 'CHOOSE_TIP',
    ACCEPT_TERMS = 'ACCEPT_TERMS',
    CHANGE_COURSE = 'CHANGE_COURSE',
    CHOOSE_PAYMENT_PROVIDER = 'CHOOSE_PAYMENT_PROVIDER',
    ORDER_PLACED = 'ORDER_PLACED',
}

interface Props {
    onClose(): void;
}

export function SpcCheckout({ onClose }: Props) {
    const router = useRouter();
    const termsPrivacy = useTermsPrivacy();
    const activeOrder = useActiveOrder();
    const { zoneId } = useZone();
    const [response, setResponse] = useState<any>({} as any);
    const [responseSuccess, setResponseSuccess] = useState<any>();
    const hasTicket = Boolean(
        activeOrder.orderProducts?.find(
            (item) =>
                item.menuProduct?.product?.type === ProductType.TICKET ||
                item.menuProduct?.product?.productModifierGroups?.find((pmg) =>
                    pmg?.modifierGroup?.modifierGroupItems?.find((mgi) => mgi?.product?.type === ProductType.TICKET),
                ),
        ),
    );
    const isRequestPhone = !response.phone && hasTicket;

    const runCheckout = useAsync(async (body) => {
        const response = await apiSpcPublicCheckoutPost({
            body: {
                isTermsAccepted: termsPrivacy.isAccepted,
                ...body,
                zoneId,
                orderProducts: activeOrder.orderProducts,
                appVersion: Environment.APP_VERSION,
                isDesktop: !isMobile(),
            },
        });

        if (response.orderId) {
            activeOrder.setPlacedOrderId(response.orderId);
        }

        setResponse(response);
    });

    useEffect(() => {
        if (isRequestPhone) {
            return;
        }

        const body: any = {};

        if (activeOrder.hasActiveOrder) {
            body.action = 'PLACE_ORDER';
        }

        runCheckout.run(body);
    }, [isRequestPhone]);

    useEffect(() => {
        if (responseSuccess) {
            const timeout = setTimeout(() => {
                if (responseSuccess.orderId) {
                    activeOrder.resetActiveOrder();

                    router.push(OrderIdPage, {
                        orderId: responseSuccess.orderId,
                    });
                } else {
                    onClose();
                }
            }, 3000);

            return () => clearTimeout(timeout);
        }
    }, [responseSuccess]);

    let element;

    if (responseSuccess) {
        const successTitle =
            responseSuccess.successTitle ?? (responseSuccess.orderId ? 'Order placed' : 'Payment successful');

        const successDescription =
            responseSuccess.successDescription ?? (responseSuccess.orderId ? 'You will be redirected shortly' : '');

        return <XOverlay type="success" title={successTitle} description={successDescription} />;
    }

    let action = response.action;

    if (isRequestPhone) {
        action = 'REQUEST_PHONE';
    }

    if (action === 'REQUEST_PHONE') {
        element = (
            <>
                <XText type={XTextType.TITLE_LARGE}>Ticket via SMS</XText>

                <SpcCheckoutPhone
                    isLoading={runCheckout.isLoading}
                    onNext={(params) => {
                        runCheckout.run({ ...response, ...params });
                    }}
                />
            </>
        );
    }

    if (action === Action.CHOOSE_PRODUCTS) {
        element = (
            <SpcCheckoutChooseProducts
                products={response.products}
                isLoading={runCheckout.isLoading}
                onNext={(products) => {
                    runCheckout.run({
                        ...response,
                        action: action,
                        products,
                    });
                }}
                onClose={onClose}
            />
        );
    }

    if (action === Action.CHOOSE_TIP) {
        element = (
            <SpcCheckoutChooseTip
                totalCost={response.totalCost}
                presets={response.presets}
                isLoading={runCheckout.isLoading}
                onNext={(tipAmount) => {
                    runCheckout.run({
                        ...response,
                        action: action,
                        tipAmount,
                    });
                }}
                onClose={onClose}
            />
        );
    }

    if (action === Action.ACCEPT_TERMS) {
        element = (
            <SpcCheckoutAcceptTerms
                termsAndConditions={response.termsAndConditions}
                privacyPolicy={response.privacyPolicy}
                totalCost={response.totalCost}
                isLoading={runCheckout.isLoading}
                onNext={() => {
                    runCheckout.run({
                        ...response,
                        isTermsAccepted: true,
                        action: action,
                    });
                }}
                onClose={onClose}
            />
        );
    }

    if (action === Action.CHANGE_COURSE) {
        element = (
            <SpcCheckoutChangeCourse
                response={response}
                isLoading={runCheckout.isLoading}
                onNext={(data) => {
                    runCheckout.run({
                        ...response,
                        ...data,
                    });
                }}
                onClose={onClose}
            />
        );
    }

    if (action === Action.CHOOSE_PAYMENT_PROVIDER) {
        element = (
            <SpcCheckoutChoosePaymentProvider
                response={response}
                isLoading={runCheckout.isLoading}
                onNext={(response) => {
                    if (response.type === 'SUCCESS') {
                        setResponseSuccess(response);
                    }
                }}
                onClose={onClose}
            />
        );
    }

    if (action === Action.ORDER_PLACED) {
        return <SpcCheckoutOrderPlaced orderId={response.orderId} />;
    }

    if (!action) {
        element = <XLoader />;
    }

    return (
        <>
            <XDrawerBottom onClose={onClose}>
                <UiGrid padding={2}>{element}</UiGrid>
            </XDrawerBottom>

            <Wrapper>
                <div className="beta" />
            </Wrapper>
        </>
    );
}
